import { styled } from '@material-ui/core';

import { domTag } from '../../../../utils/styledFilter';

// The map card is the one memorable element on the step, so everything here
// stays quiet: tokenised surfaces, one accent (the brand), sentence-case copy.

export const MapFigure = styled('figure')({
	display: 'grid',
	gap: 12,
	margin: 0,
	padding: 14,
	boxSizing: 'border-box',
	width: '100%',
	minWidth: 0,
	border: '1px solid var(--color-border-default)',
	borderRadius: 14,
	backgroundColor: 'var(--color-bg-secondary)',
});

// Reserves the image's box before it loads so the step never jumps.
export const SnapshotFrame = styled('div')({
	'position': 'relative',
	'width': '100%',
	'minWidth': 0,
	'overflow': 'hidden',
	'borderRadius': 10,
	'backgroundColor': 'var(--color-bg-tertiary)',
	'border': '1px solid var(--color-border-subtle)',
	'& > img': {
		display: 'block',
		width: '100%',
		height: 'auto',
	},
});

export const FallbackPane = styled('p')({
	position: 'absolute',
	inset: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	margin: 0,
	padding: 16,
	textAlign: 'center',
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-sm-size)',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-secondary)',
});

export const Caption = styled('figcaption')({
	'display': 'grid',
	'gap': 4,
	'minWidth': 0,
	'& .distance-line': {
		// One line is always reserved so the walk estimate can append without a jump.
		minHeight: 'calc(var(--text-body-size) * var(--leading-normal))',
		margin: 0,
		fontFamily: 'var(--font-family-medium)',
		fontWeight: 500,
		fontSize: 'var(--text-body-size)',
		lineHeight: 'var(--leading-normal)',
		color: 'var(--color-text-primary)',
		overflowWrap: 'anywhere',
	},
	'& .walk': {
		fontFamily: 'var(--font-family-regular)',
		fontWeight: 400,
		color: 'var(--color-text-secondary)',
	},
	'& .accuracy': {
		margin: 0,
		fontFamily: 'var(--font-family-regular)',
		fontSize: 'var(--text-body-sm-size)',
		lineHeight: 'var(--leading-normal)',
		color: 'var(--color-text-tertiary)',
	},
});

export const Legend = styled('ul')({
	'display': 'flex',
	'flexWrap': 'wrap',
	'gap': '6px 18px',
	'margin': 0,
	'padding': 0,
	'listStyle': 'none',
	'fontFamily': 'var(--font-family-regular)',
	'fontSize': 'var(--text-body-sm-size)',
	'lineHeight': 'var(--leading-normal)',
	'color': 'var(--color-text-secondary)',
	'& li': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
	},
});

export const LegendDot = styled(domTag('span', ['tone']))(({ tone }: { tone: 'you' | 'unit' }) => ({
	display: 'inline-block',
	width: 12,
	height: 12,
	flexShrink: 0,
	borderRadius: tone === 'you' ? '50%' : 3,
	backgroundColor: tone === 'you' ? 'var(--color-text-primary)' : 'var(--color-brand-primary)',
	boxShadow: tone === 'you' ? '0 0 0 2px var(--color-bg-elevated)' : 'none',
}));

export const Actions = styled('div')({
	display: 'flex',
	flexWrap: 'wrap',
	gap: 8,
});

const actionBase = {
	'display': 'inline-flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'gap': 8,
	'minHeight': 44,
	'minWidth': 44,
	'padding': '10px 16px',
	'boxSizing': 'border-box' as const,
	'borderRadius': 'var(--radius-lg)',
	'border': '1px solid var(--color-border-default)',
	'backgroundColor': 'var(--color-bg-elevated)',
	'color': 'var(--color-text-primary)',
	'fontFamily': 'var(--font-family-medium)',
	'fontWeight': 500,
	'fontSize': 'var(--text-body-sm-size)',
	'lineHeight': 'var(--leading-snug)',
	'textDecoration': 'none',
	'cursor': 'pointer',
	'transition': 'background-color 150ms ease, border-color 150ms ease',
	'&:hover': {
		borderColor: 'var(--color-brand-primary)',
	},
	'&:focus-visible': {
		outline: '2px solid var(--color-brand-primary)',
		outlineOffset: 2,
	},
	'&:disabled': {
		cursor: 'default',
		opacity: 0.6,
	},
};

export const ActionLink = styled('a')(actionBase);

export const ActionButton = styled('button')(actionBase);

// Ward / LGA choices and GPS states render nothing; this is the one "quiet"
// notice: a unit INEC has not mapped yet.
export const QuietNote = styled('p')({
	margin: 0,
	padding: '12px 14px',
	border: '1px dashed var(--color-border-default)',
	borderRadius: 10,
	backgroundColor: 'var(--color-bg-secondary)',
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-sm-size)',
	lineHeight: 'var(--leading-normal)',
	color: 'var(--color-text-secondary)',
});

export const ExploreShell = styled('div')({
	'position': 'relative',
	'width': '100%',
	'minWidth': 0,
	'height': 'clamp(220px, 45svh, 360px)',
	'overflow': 'hidden',
	'borderRadius': 10,
	'border': '1px solid var(--color-border-subtle)',
	'backgroundColor': 'var(--color-bg-tertiary)',
	'&:focus-visible': {
		outline: '2px solid var(--color-brand-primary)',
		outlineOffset: 2,
	},
	// Leaflet's default pane text inherits page colour; keep attribution legible on both tiles.
	'& .leaflet-container': {
		fontFamily: 'var(--font-family-regular)',
		backgroundColor: 'var(--color-bg-tertiary)',
	},
	'& .unit-distance-pin': {
		background: 'none',
		border: 'none',
	},
});

export const ExploreToolbar = styled('div')({
	position: 'absolute',
	top: 10,
	right: 10,
	// Above Leaflet's panes (z-index 400) and controls (1000).
	zIndex: 1001,
});
